/**
 * Judge.me ARIA Accessibility Patch
 * ----------------------------------
 * Judge.me's widget is Vue-rendered and re-mounts on tab switches,
 * review reloads, and filter changes. This script uses a MutationObserver
 * to catch every render and patch known ARIA/semantic issues.
 *
 * Fixes:
 * 1. Carousel <div> with tabindex="0" but no role -> adds role="region"
 * 2. Rating number styled as a heading (.jm-text with heading CSS vars)
 *    -> hides from AT if a labeled sibling already announces the rating,
 *       otherwise adds an aria-label directly
 * 3. (Diagnostic only) Logs tab elements whose aria-controls target
 *    doesn't exist yet, so you can confirm the Issue #1 tabpanel timing
 *    problem before/after reporting it to Judge.me
 *
 * Install: paste into your theme's global JS file (e.g. theme.js,
 * global.js) or add as a small inline <script> in theme.liquid,
 * loaded on any page where Judge.me widgets can appear.
 *
 * Performance note: the MutationObserver is scoped to Judge.me's own
 * container(s) rather than document.body, so it isn't reacting to every
 * DOM change on the page. Adjust CONTAINER_SELECTORS below if your
 * theme places Judge.me widgets somewhere not covered by the defaults.
 */

(function () {
  'use strict';

  // Judge.me mount points, confirmed via DevTools inspection on a live
  // GPI/GPRO product page. #judgeme_product_reviews is the real widget
  // wrapper (class="jdgm-widget jdgm-review-widget jdgm--done-setup-widget
  // jdgm-widget-revamp"). The shopify-block selector is a fallback in case
  // Judge.me ever renders elements (carousel, tabs) outside that inner div
  // on other page types/sections.
  var CONTAINER_SELECTORS = [
    '#judgeme_product_reviews',
    '[id^="shopify-block-"][id*="judge_me_reviews"]',
    '.jdgm-widget-revamp'
  ];

  function patchCarousels(root) {
    root.querySelectorAll('.jm-carousel[tabindex]:not([role])').forEach(function (el) {
      el.setAttribute('role', 'region');
      el.setAttribute('aria-roledescription', 'carousel');
      el.setAttribute('aria-label', 'Product reviews carousel');
    });
  }

  // Matches things like "5", "5.0", "4.74" - i.e. an actual rating number,
  // not section titles like "Customer Reviews" which also happen to use
  // Judge.me's heading-style CSS variables.
  var RATING_NUMBER_PATTERN = /^\d(\.\d+)?$/;

  function patchHeadingStyledText(root) {
    root.querySelectorAll('.jm-text[style*="--jm-heading"]:not([data-lks-patched])').forEach(function (el) {
      var text = el.textContent.trim();

      // Only touch elements whose text content is actually a rating number.
      // Judge.me reuses the same heading-style CSS vars for other text
      // (e.g. the "Customer Reviews" section title), which must NOT be
      // relabeled as a rating.
      if (!RATING_NUMBER_PATTERN.test(text)) return;

      el.setAttribute('data-lks-patched', 'true');

      // Look for a nearby element that already labels the rating for AT
      // (Judge.me claims to add aria-labels to star ratings elsewhere).
      var container = el.closest('.jdgm-widget, .jm-widget, [class*="jdgm"], [class*="jm-"]');
      var hasLabelSibling = container && container.querySelector('[aria-label*="star" i], [aria-label*="rating" i]');

      if (hasLabelSibling) {
        // Redundant with the labeled sibling: hide this styled number from AT.
        el.setAttribute('aria-hidden', 'true');
      } else {
        // No existing label found: label it directly instead of hiding it.
        if (text) {
          el.setAttribute('role', 'text');
          el.setAttribute('aria-label', 'Average rating ' + text + ' out of 5');
        }
      }
    });
  }

  function diagnoseTabControls(root) {
    root.querySelectorAll('[role="tab"][aria-controls]').forEach(function (el) {
      var targetId = el.getAttribute('aria-controls');
      if (targetId && !document.getElementById(targetId)) {
        console.warn(
          '[JM ARIA patch] Tab references missing aria-controls target:',
          targetId,
          '- this is a Judge.me render-timing issue, not fixable via CSS/JS patch alone.',
          el
        );
      }
    });
  }

  function runAllPatches(root) {
    root = root || document;
    patchCarousels(root);
    patchHeadingStyledText(root);
    diagnoseTabControls(root);
  }

  // Debounce so rapid successive mutations (e.g. Vue re-render bursts)
  // don't trigger the patch dozens of times per second.
  var debounceTimer = null;
  function scheduleRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      runAllPatches(document);
    }, 150);
  }

  function findContainers() {
    var found = [];
    CONTAINER_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        if (found.indexOf(el) === -1) found.push(el);
      });
    });
    return found;
  }

  var scopedObserver = new MutationObserver(scheduleRun);
  var bootstrapObserver = null;
  var scopedTargets = [];

  function attachScopedObserver() {
    var containers = findContainers();
    if (containers.length === 0) return false;

    scopedTargets = containers;
    containers.forEach(function (container) {
      scopedObserver.observe(container, { childList: true, subtree: true });
    });
    return true;
  }

  // Initial run in case Judge.me has already rendered by the time this loads.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    scheduleRun();

    if (attachScopedObserver()) return;

    // Judge.me's container isn't in the DOM yet (common: the app injects
    // its own wrapper div a moment after page load). Use a lightweight,
    // temporary body-level observer *only* until the real container
    // appears, then switch to the scoped one and stop watching the body.
    bootstrapObserver = new MutationObserver(function () {
      if (attachScopedObserver()) {
        bootstrapObserver.disconnect();
        bootstrapObserver = null;
        scheduleRun();
      }
    });
    bootstrapObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
